import {
  CONTAINER_ICON_WIDTH,
  DOCK_PADDING,
  LERP_ACTIVE,
  LERP_IDLE,
  MIN_SCALE,
  POSITION_THRESHOLD,
  SCALE_THRESHOLD,
} from "./dock-config"
import { useCallback, useEffect, useRef, useState } from "react"

export interface UseDockAnimationOptions {
  itemCount: number
  maxScale: number
  effectWidth: number
  baseSpacing: number
  dockRef: React.RefObject<HTMLDivElement | null>
}

export function useDockAnimation({
  itemCount,
  maxScale,
  effectWidth,
  baseSpacing,
  dockRef,
}: UseDockAnimationOptions) {
  const [mouseX, setMouseX] = useState<number | null>(null)
  const [currentScales, setCurrentScales] = useState<number[]>(
    Array.from({ length: itemCount }, () => MIN_SCALE)
  )
  const [currentPositions, setCurrentPositions] = useState<number[]>([])
  const iconRefs = useRef<(HTMLDivElement | null)[]>([])
  const animationFrameRef = useRef<number | undefined>(undefined)
  const lastMouseMoveTime = useRef(0)

  const calculateTargetMagnification = useCallback(
    (mousePosition: number | null) => {
      if (mousePosition === null) {
        return Array.from({ length: itemCount }, () => MIN_SCALE)
      }
      return Array.from({ length: itemCount }, (_, index) => {
        const iconCenter =
          index * (CONTAINER_ICON_WIDTH + baseSpacing) +
          CONTAINER_ICON_WIDTH / 2
        const minX = mousePosition - effectWidth / 2
        const maxX = mousePosition + effectWidth / 2
        if (iconCenter < minX || iconCenter > maxX) return MIN_SCALE
        const theta = ((iconCenter - minX) / effectWidth) * 2 * Math.PI
        const capped = Math.min(Math.max(theta, 0), 2 * Math.PI)
        const factor = (1 - Math.cos(capped)) / 2
        return MIN_SCALE + factor * (maxScale - MIN_SCALE)
      })
    },
    [itemCount, baseSpacing, effectWidth, maxScale]
  )

  const calculatePositions = useCallback(
    (scales: number[]) => {
      let x = 0
      return scales.map((scale) => {
        const w = CONTAINER_ICON_WIDTH * scale
        const centerX = x + w / 2
        x += w + baseSpacing
        return centerX
      })
    },
    [baseSpacing]
  )

  useEffect(() => {
    const scales = Array.from({ length: itemCount }, () => MIN_SCALE)
    setCurrentScales(scales)
    setCurrentPositions(calculatePositions(scales))
  }, [itemCount, maxScale, effectWidth, baseSpacing, calculatePositions])

  const animateToTarget = useCallback(() => {
    const targetScales = calculateTargetMagnification(mouseX)
    const targetPositions = calculatePositions(targetScales)
    const lerp = mouseX !== null ? LERP_ACTIVE : LERP_IDLE

    setCurrentScales((prev) =>
      prev.map((s, i) => s + (targetScales[i] - s) * lerp)
    )
    setCurrentPositions((prev) =>
      prev.map((p, i) => p + (targetPositions[i] - p) * lerp)
    )

    const scalesDone = targetScales.every(
      (t, i) => Math.abs(currentScales[i] - t) <= SCALE_THRESHOLD
    )
    const positionsDone = targetPositions.every(
      (t, i) => Math.abs(currentPositions[i] - t) <= POSITION_THRESHOLD
    )
    if (!scalesDone || !positionsDone || mouseX !== null) {
      animationFrameRef.current = requestAnimationFrame(animateToTarget)
    }
  }, [
    mouseX,
    calculateTargetMagnification,
    calculatePositions,
    currentScales,
    currentPositions,
  ])

  useEffect(() => {
    animationFrameRef.current = requestAnimationFrame(animateToTarget)
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [animateToTarget])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const now = performance.now()
      if (now - lastMouseMoveTime.current < 16) return
      lastMouseMoveTime.current = now
      if (dockRef.current) {
        const rect = dockRef.current.getBoundingClientRect()
        setMouseX(e.clientX - rect.left - DOCK_PADDING)
      }
    },
    [dockRef]
  )

  const handleMouseLeave = useCallback(() => setMouseX(null), [])

  const contentWidth =
    currentPositions.length > 0
      ? Math.max(
          ...currentPositions.map(
            (pos, i) =>
              pos + (CONTAINER_ICON_WIDTH * (currentScales[i] ?? 1)) / 2
          )
        )
      : itemCount * (CONTAINER_ICON_WIDTH + baseSpacing) - baseSpacing

  return {
    currentScales,
    currentPositions,
    contentWidth,
    handleMouseMove,
    handleMouseLeave,
    setMouseX,
    iconRefs,
  }
}
